import { DoctorAvatar } from "../components/DoctorAvatar";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, Activity, ShieldCheck, Clock, CheckCircle2, ChevronRight, Calendar, Award } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function CardiologyPage({ onBookDoctor, onBookTest }) {
  const [cardiacTests, setCardiacTests] = useState([]);
  const [cardiologists, setCardiologists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCardiacData();
  }, []);

  const fetchCardiacData = async () => {
    try {
      const [testsRes, docRes] = await Promise.all([
        axios.get(`${API}/diagnostics?category=Cardiology`),
        axios.get(`${API}/doctors?specialty=Cardiology`)
      ]);
      setCardiacTests(testsRes.data || []);
      setCardiologists(docRes.data || []);
    } catch (err) {
      console.error("Error loading cardiology data:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-12">
      
      {/* Hero Banner for Heart & Cardiac Diagnostics */}
      <div className="bg-gradient-to-r from-[#0D3B2E] via-[#07221A] to-[#0D3B2E] text-white p-8 sm:p-14 rounded-3xl relative overflow-hidden shadow-2xl">
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 bg-rose-500/20 border border-rose-400/40 text-rose-300 px-3 py-1 rounded-full text-xs font-semibold">
            <Heart className="w-3.5 h-3.5 fill-rose-400 text-rose-400" />
            Dedicated Clinical Wing • 6SigmaCare Kolkata
          </div>

          <h1 className="font-serif-luxury text-3xl sm:text-5xl font-bold text-white tracking-tight leading-tight">
            Heart & Advanced Cardiac Diagnostics
          </h1>

          <p className="text-xs sm:text-sm text-emerald-100/90 leading-relaxed max-w-2xl">
            Non-invasive cardiology diagnostics with state-of-the-art 2D Echocardiography, Colour Doppler, Treadmill Stress Testing (TMT), and 24-hour ambulatory Holter monitoring supervised by veteran interventional cardiologists.
          </p>

          <div className="pt-2 flex flex-wrap gap-3">
            <button
              onClick={() => onBookDoctor ? onBookDoctor({ _picker: true, specialty: "Cardiology" }) : null}
              data-testid="cardio-book-specialist-btn"
              className="px-6 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-[#07221A] font-bold text-xs shadow-lg transition-all flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              Consult Senior Cardiologist
            </button>
            <Link
              to="/packages#cardiac-health-package"
              className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-xs border border-white/20 transition-all"
            >
              View Cardiac Health Package
            </Link>
          </div>
        </div>
      </div>

      {/* 6 Key Cardiac Diagnostics Modalities */}
      <div className="space-y-6">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#3B8271]">Non-Invasive Diagnostic Suite</span>
          <h2 className="font-serif-luxury text-2xl sm:text-3xl font-bold text-[#0D3B2E]">
            Cardiac Investigation Modalities
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cardiacTests.map((test) => (
            <div
              key={test.id}
              data-testid={`cardiac-test-card-${test.id}`}
              className="bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl transition-all p-6 flex flex-col justify-between group"
            >
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
                    <Activity className="w-5 h-5" />
                  </div>
                  <span className="text-[11px] text-emerald-800 font-bold bg-emerald-50 px-2 py-0.5 rounded-full">
                    {test.discount_pct}% OFF
                  </span>
                </div>

                <h3 className="font-serif-luxury text-lg font-bold text-gray-900 group-hover:text-[#0D3B2E] transition-colors">
                  {test.name}
                </h3>

                <p className="text-xs text-gray-500 leading-relaxed">
                  {test.description}
                </p>

                <div className="p-2.5 bg-[#FBF9F4] rounded-xl border border-gray-100 text-[11px] text-gray-600 space-y-0.5">
                  <p><b>TAT:</b> {test.tat}</p>
                  <p><b>Preparation:</b> {test.preparation_instructions}</p>
                </div>
              </div>

              <div className="pt-4 mt-4 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-400 line-through mr-2">₹{test.mrp}</span>
                  <span className="font-serif-luxury text-xl font-bold text-[#0D3B2E]">₹{test.price}</span>
                </div>

                <button
                  onClick={() => onBookTest ? onBookTest(test) : null}
                  data-testid={`cardio-book-test-${test.id}`}
                  className="px-4 py-2 bg-[#0D3B2E] hover:bg-[#165644] text-white text-xs font-semibold rounded-xl transition-colors shadow"
                >
                  Book Test
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Senior Cardiologist Faculty */}
      {cardiologists.length > 0 && (
        <div className="bg-[#FBF9F4] p-8 rounded-3xl border border-gray-200 space-y-6">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#3B8271]">Consultation Chambers</span>
            <h3 className="font-serif-luxury text-2xl font-bold text-[#0D3B2E]">
              Consulting Cardiologists
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cardiologists.map((doc) => (
              <div key={doc.id} className="p-5 bg-white rounded-2xl border border-gray-200 flex gap-4 items-start shadow-sm">
                <DoctorAvatar doctor={doc} className="w-16 h-16 rounded-2xl border" textClass="text-xl" />
                <div className="space-y-1">
                  <h4 className="font-serif-luxury text-base font-bold text-gray-900">{doc.name}</h4>
                  <p className="text-[11px] text-[#3B8271] font-medium">{doc.qualification}</p>
                  <p className="text-xs text-gray-500 line-clamp-2">{doc.about}</p>
                  <button
                    onClick={() => onBookDoctor ? onBookDoctor(doc) : null}
                    className="mt-2 text-xs font-bold text-[#0D3B2E] hover:underline flex items-center gap-1"
                  >
                    Book OPD Slot (₹{doc.physical_fee}) <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
